import React, {Component} from 'react'
import propTypes from 'prop-types'
import {isEmpty, isNil} from 'lodash'
import Checkbox from '@material-ui/core/Checkbox'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import DatePickerRangeSelectField from 'components/datePickerRangeSelectField'
import CompanySelectField from 'components/companySelectField'
import {SelectField} from 'components/selectField'
import {prefix} from 'helpers/component'
import {getQuoteTypes, PersonTypes, QuoteDateTypes, QuoteStatus} from 'helpers/quotes'
import {getContactName} from 'helpers/contacts'
import {getJobStatusOptionRender} from 'helpers/optionRenders'
import {isPartDeliveredOptionDisabled} from 'helpers/deliveries'
import {getFilterBarFilters} from 'helpers/filter'
import {getOrderTypeOptions} from "helpers/quotes"
import {UnallocatedOption} from 'helpers/opportunities'
import {trackEvent} from 'helpers/tracking'
import './quotingFilterSidebar.scss'

const filterCheckboxStyle = {height: 30}

class QuotingFilterSidebar extends Component {
  constructor(props) {
    super(props)
    this.getClass = prefix('quoting-filter-sidebar-')

    this.updateFilters = this.props.onUpdateFiltersFromDeliveries || this.props.onUpdateFilters
  }

  onChangeOptions = (key, options) => {
    this.onUpdateFilters(getFilterBarFilters(key, options))
  }

  onChangeOption = (key, value) => {
    this.onUpdateFilters({
      [key]: value,
    })
  }

  getContactOptions = () => {
    const {homeContacts} = this.props
    return homeContacts.map(contact => ({
      value: contact.id,
      label: getContactName(contact),
    }))
  }

  onChangeTarget = (targetStart, targetEnd, timeKey) => {
    this.onUpdateFilters({
      targetStart: !isNil(timeKey) ? null : targetStart,
      targetEnd: !isNil(timeKey) ? null : targetEnd,
      timeKey: isNil(timeKey) ? null : timeKey,
    })
  }

  onChangeCompanies = (options) => {
    this.onUpdateFilters({
      companies: options,
    })
  }

  onUpdateFilters = (filter) => {
    trackEvent('Quoting Filter Sidebar', 'Changed a filter', 'Filter')
    this.updateFilters(filter)
  }

  getQuoteStatusOptions = (statusOptions) => {
    const {user} = this.props

    const statusesToExclude = [
      QuoteStatus.allQuotes,
      QuoteStatus.activeQuotes,
      QuoteStatus.recentlyOpened,
      QuoteStatus.unsent,
      QuoteStatus.unconfirmed,
      QuoteStatus.requiredSoon,
      QuoteStatus.expiring,
      QuoteStatus.expired,
      QuoteStatus.notAccepted,
      QuoteStatus.inProduction,
      QuoteStatus.inDelivery,
    ]
    return (!isEmpty(statusOptions)
        ? getQuoteTypes().filter(quoteType => statusOptions.includes(quoteType.id))
        : getQuoteTypes().filter(quoteType => !statusesToExclude.includes(quoteType.id))
    ).map(type => ({
      value: type.id,
      label: type.title,
      disabled: isPartDeliveredOptionDisabled(type.id, user),
    }))
  }

  getStatusFilter = () => {
    const {statusOptions, statusTypes} = this.props

    return <SelectField
      className={'status'}
      label={'Status'}
      placeholder={'All Quotes'}
      multi={true}
      options={this.getQuoteStatusOptions(statusOptions)}
      OptionRenderer={(option) => getJobStatusOptionRender(option)}
      value={statusTypes}
      onChange={(options) => this.onChangeOptions('statusTypes', options)}/>
  }

  getCompaniesFilter = () => {
    const {companies} = this.props

    return <CompanySelectField
      className={'companies'}
      label={'Companies'}
      key={'companies'}
      fields={{
        id: true,
        name: true,
      }}
      params={{
        mandatory: true,
      }}
      multi={true}
      value={companies}
      onChange={(v) => this.onChangeCompanies(v)}/>
  }

  getPrimaryUsersFilter = () => {
    const {userTypes} = this.props

    return <SelectField
      className={'users'}
      label={'Users'}
      placeholder={'All Roles'}
      multi={true}
      clearable={false}
      options={PersonTypes}
      value={userTypes}
      onChange={(options) => this.onChangeOptions('userTypes', options)}/>
  }

  getSecondaryUsersFilter = () => {
    const {contacts, userTypes} = this.props

    return <SelectField
      className={'home-contacts'}
      placeholder={'Whole Company'}
      multi={true}
      options={this.getContactOptions()}
      disabled={isEmpty(userTypes)}
      value={contacts}
      onChange={(options) => this.onChangeOptions('contacts', options)}/>
  }

  getJobTypeFilter = () => {
    const {orderTypeId, orderTypes} = this.props

    return <SelectField
      className={'orderType'}
      label={'Job Type'}
      placeholder={'All Job Types'}
      clearable={true}
      multi={true}
      options={[
        UnallocatedOption,
        ...getOrderTypeOptions(orderTypes),
      ]}
      value={orderTypeId}
      onChange={(options) => this.onChangeOptions('orderTypeId', options)}/>
  }

  getPrimaryDatesFilter = () => {
    const {dateOptions, dateTypes} = this.props

    return <SelectField
      className={'dates'}
      label={'Date'}
      placeholder={'All Dates'}
      multi={true}
      clearable={false}
      options={!isEmpty(dateOptions) ? dateOptions : QuoteDateTypes}
      value={dateTypes}
      onChange={(options) => this.onChangeOptions('dateTypes', options)}/>
  }

  getSecondaryDatesFilter = () => {
    const {dateTypes, presetType, targetEnd, targetStart, timeKey} = this.props

    return <DatePickerRangeSelectField
      startDate={targetStart}
      endDate={targetEnd}
      timeKey={timeKey}
      showPresets={true}
      presetTypes={presetType}
      disabled={isEmpty(dateTypes)}
      onChange={this.onChangeTarget}/>
  }

  getAllRevisionsFilter = () => {
    const {allRevisions, allowAllRevisions} = this.props

    return allowAllRevisions &&
      <FormControlLabel
        className={'all-revisions'}
        control={
          <Checkbox
            style={filterCheckboxStyle}
            checked={allRevisions}
            onChange={(event, isInputChecked) => this.onChangeOption('allRevisions', isInputChecked)}
          />
        }
        label={'All Revisions'}
      />
  }

  getIncludeFullyScheduledFilter = () => {
    const {allowIncludeFullyScheduled, includeFullyScheduled} = this.props

    return allowIncludeFullyScheduled &&
      <FormControlLabel
        className={'include-fully-scheduled'}
        control={
          <Checkbox
            style={filterCheckboxStyle}
            checked={includeFullyScheduled}
            onChange={(event, isInputChecked) => {
              this.onChangeOption('includeFullyScheduled', isInputChecked)
              !isInputChecked ? this.onChangeOption('includeFullyDelivered', false) : null
            }}
          />
        }
        label={'Include Fully Scheduled'}
      />
  }

  getIncludeFullyDeliveredFilter = () => {
    const {allowIncludeFullyDelivered, includeFullyDelivered, includeFullyScheduled, statusTypes} = this.props

    return allowIncludeFullyDelivered && (
      <FormControlLabel
        className={'include-fully-delivered'}
        control={
          <Checkbox
            style={filterCheckboxStyle}
            checked={includeFullyDelivered}
            disabled={!includeFullyScheduled}
            onChange={(event, isInputChecked) => {
              this.onChangeOption('includeFullyDelivered', isInputChecked)
              if (!isInputChecked) {
                this.onChangeOption(
                  'statusTypes',
                  statusTypes.filter(status => status !== QuoteStatus.delivered)
                )
              }
            }}
          />
        }
        label={'Include Fully Delivered'}
      />
    )
  }

  render() {
    return (
      <div className={this.getClass('sidebar')}>
        <div className={this.getClass('container')}>
          <div className="filters">
            {this.getStatusFilter()}
            {this.getCompaniesFilter()}
            {this.getPrimaryUsersFilter()}
            {this.getSecondaryUsersFilter()}
            {this.getJobTypeFilter()}
            {this.getPrimaryDatesFilter()}
            {this.getSecondaryDatesFilter()}
            {this.getAllRevisionsFilter()}
            {this.getIncludeFullyScheduledFilter()}
            {this.getIncludeFullyDeliveredFilter()}
          </div>
        </div>
      </div>
    )
  }
}

QuotingFilterSidebar.propTypes = {
  allowAllRevisions: propTypes.bool,
  allowIncludeFullyScheduled: propTypes.bool,
  allRevisions: propTypes.bool,
  companies: propTypes.array.isRequired,
  contacts: propTypes.array.isRequired,
  dateOptions: propTypes.array,
  dateTypes: propTypes.array.isRequired,
  homeContacts: propTypes.array.isRequired,
  includeFullyScheduled: propTypes.bool,
  onUpdateFilters: propTypes.func.isRequired,
  onUpdateFiltersFromDeliveries: propTypes.func,
  orderTypes: propTypes.array,
  orderTypeId: propTypes.array,
  pivotTableKey: propTypes.string.isRequired,
  presetType: propTypes.string,
  search: propTypes.string,
  statusOptions: propTypes.array,
  statusTypes: propTypes.array.isRequired,
  targetEnd: propTypes.number,
  targetStart: propTypes.number,
  timeKey: propTypes.string,
  user: propTypes.object.isRequired,
  userTypes: propTypes.array.isRequired,
  allowIncludeFullyDelivered: propTypes.bool,
  includeFullyDelivered: propTypes.bool,
}

QuotingFilterSidebar.defaultProps = {
  allowAllRevisions: true,
  allowIncludeFullyScheduled: false,
  allowIncludeFullyDelivered: false,
  companies: [],
  contacts: [],
  dateOptions: [],
  dateTypes: [],
  homeContacts: [],
  presetType: 'default',
  statusOptions: [],
  statusTypes: [],
  userTypes: [],
}

export default QuotingFilterSidebar
